// Display formats for workbook values: the same rules in grids, pivots, charts, the status bar and exports.
import type { XFormat, XOutColumn } from "../api";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const nf = (min: number, max: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });
const F = { money: nf(2, 2), qty: nf(0, 2), int: nf(0, 0), pct: nf(1, 1), num: nf(0, 4) };

export function fmtDate(v: string) {
  const [y, m, d] = v.slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}
/** A grouped date key by its bucket: "Oct 2026", "Q4 2026", "Week of 10/19/2026", "2026". */
export function fmtBucket(v: unknown, bucket?: string) {
  if (v === null || v === undefined) return "(Blank)";
  const s = String(v);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  const y = s.slice(0, 4), m = Number(s.slice(5, 7));
  if (bucket === "year") return y;
  if (bucket === "quarter") return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  if (bucket === "month") return `${MON[m - 1]} ${y}`;
  if (bucket === "week") return `Week of ${fmtDate(s)}`;
  return fmtDate(s);
}
export function fmt(v: unknown, col?: Pick<XOutColumn, "type" | "format" | "bucket">, override?: XFormat): string {
  if (v === null || v === undefined || v === "") return "";
  const f = override ?? col?.format;
  if (col?.bucket) return fmtBucket(v, col.bucket);
  if (typeof v === "boolean" || f === "bool" || col?.type === "bool") return v === true || v === "true" ? "Yes" : "No";
  if (col?.type === "date" || f === "date") return typeof v === "string" ? fmtDate(v) : String(v);
  if (typeof v === "number" || col?.type === "number") {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (f === "pct") return `${F.pct.format(n * 100)}%`;
    if (f === "money") return F.money.format(n);
    if (f === "int") return F.int.format(n);
    if (f === "qty") return F.qty.format(n);
    return F.num.format(n);
  }
  return String(v);
}
/** A key cell: blank shows as (Blank) so a group of empties is visible. */
export const fmtKey = (v: unknown, col: XOutColumn) => (v === null || v === undefined || v === "" ? "(Blank)" : fmt(v, col));
/** Plain value for copy and CSV: numbers unformatted, dates ISO, so Excel reads them as numbers and dates. */
export const raw = (v: unknown) => (v === null || v === undefined ? "" : typeof v === "boolean" ? (v ? "TRUE" : "FALSE") : String(v));
export const isNumCol = (c?: Pick<XOutColumn, "type">) => c?.type === "number";
/** A headline number, compacted: 1,284 · 12.9K · 4.2M; percentages to one decimal. */
export function fmtCompact(v: unknown, format?: XFormat | string): string {
  if (v === null || v === undefined || v === "") return "–";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (format === "pct") return `${F.pct.format(n * 100)}%`;
  const a = Math.abs(n);
  const c = (d: number, s: string) => `${(n / d).toLocaleString("en-US", { minimumFractionDigits: a / d >= 100 ? 0 : 1, maximumFractionDigits: a / d >= 100 ? 0 : a / d >= 10 ? 1 : 2 })}${s}`;
  if (a >= 1e9) return c(1e9, "B");
  if (a >= 1e6) return c(1e6, "M");
  if (a >= 1e4) return c(1e3, "K");
  if (format === "money") return n.toLocaleString("en-US", { maximumFractionDigits: a >= 100 ? 0 : 2 });
  return n.toLocaleString("en-US", { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });
}
/** "Aug 2025 – Jul 2026", "Q3 2026", "Jan 1 – Sep 30, 2026". */
export function fmtRange(from: string, to: string) {
  const [fy, fm, fd] = from.split("-").map(Number), [ty, tm, td] = to.split("-").map(Number);
  const lastDay = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  if (fd === 1 && td === lastDay) {
    if (fy === ty && fm === tm) return `${MON[fm - 1]} ${fy}`;
    return fy === ty ? `${MON[fm - 1]} – ${MON[tm - 1]} ${ty}` : `${MON[fm - 1]} ${fy} – ${MON[tm - 1]} ${ty}`;
  }
  return fy === ty ? `${MON[fm - 1]} ${fd} – ${MON[tm - 1]} ${td}, ${ty}` : `${MON[fm - 1]} ${fd}, ${fy} – ${MON[tm - 1]} ${td}, ${ty}`;
}
/** A headline number with its unit: $1.24M, 31.2%, 4,210. */
export function fmtPlain(v: number | null | undefined, format?: XFormat | string | null) {
  if (v === null || v === undefined || !Number.isFinite(v)) return "–";
  return format === "money" ? `${v < 0 ? "−" : ""}$${fmtCompact(Math.abs(v), "money")}` : fmtCompact(v, format ?? undefined);
}
/** A change with its sign: +$140K, −2.1 pts, +320. */
export function fmtSigned(v: number | null | undefined, format?: XFormat | string | null) {
  if (v === null || v === undefined || !Number.isFinite(v)) return "–";
  const s = v > 0 ? "+" : v < 0 ? "−" : "", a = Math.abs(v);
  if (format === "pct") return `${s}${(a * 100).toFixed(1)} pts`;
  return `${s}${format === "money" ? "$" : ""}${fmtCompact(a, format === "money" ? "money" : format ?? undefined)}`;
}
